var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { AspectRatioCropper, cropImage, canvasToFile } from '@rad/components/inputs/aspect-ratio-cropper';
import { getMimeTypeFromFilename } from '@rad/utility/formatting';
import Modal from '@rad/components/ui/modal';
const loadImage = (buffer, mimeType) => {
    const blob = new Blob([buffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        img.src = url;
    });
};
export const useCropModal = (config) => {
    const { aspectRatio, targetWidth = 1920, targetHeight = 1080, tolerance = 0.01, } = config;
    const resolverRef = React.useRef(null);
    const cropDataRef = React.useRef(null);
    const [image, setImage] = React.useState(null);
    const [filename, setFilename] = React.useState('');
    const isOpen = image !== null;
    const handleCropChange = React.useCallback((data) => {
        cropDataRef.current = data;
    }, []);
    const handleSave = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!image || !cropDataRef.current || !resolverRef.current)
            return;
        const canvas = yield cropImage(image, cropDataRef.current, targetWidth, targetHeight);
        const mimeType = getMimeTypeFromFilename(filename);
        const browserFile = yield canvasToFile(canvas, filename, mimeType);
        resolverRef.current(browserFile);
        resolverRef.current = null;
        cropDataRef.current = null;
        setImage(null);
        setFilename('');
    }), [image, filename, targetWidth, targetHeight]);
    const handleClose = React.useCallback(() => {
        var _a;
        (_a = resolverRef.current) === null || _a === void 0 ? void 0 : _a.call(resolverRef, null);
        resolverRef.current = null;
        cropDataRef.current = null;
        setImage(null);
        setFilename('');
    }, []);
    const cropStep = React.useCallback((file) => __awaiter(void 0, void 0, void 0, function* () {
        const buffer = yield file.arrayBuffer();
        const mimeType = getMimeTypeFromFilename(file.name);
        const img = yield loadImage(buffer, mimeType);
        const currentRatio = img.width / img.height;
        if (Math.abs(currentRatio - aspectRatio) / aspectRatio < tolerance) {
            return file;
        }
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setImage(img);
            setFilename(file.name);
        });
    }), [aspectRatio, tolerance]);
    const modalElement = (_jsx(Modal, { isOpen: isOpen, title: "Crop Image", onClose: handleClose, footer: _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleSave, children: "Apply Crop" }), children: image && (_jsx(AspectRatioCropper, { image: image, aspectRatio: aspectRatio, onCropChange: handleCropChange })) }));
    return { cropStep, modalElement };
};
